import { i18n } from '@src/i18n-setup';
import { isNil, isEmpty } from 'lodash';
import OfferStateEnum from '@enums/offer-state-enum';
import UpdateTypeEnum, { getUpdateType } from '@enums/update-type-enum';
import getDateString from '@utils/date/get-date-string';
import getShortDateString from '@utils/date/get-short-date-string';
import getTimeDistance from '@utils/date/get-time-distance';

const AUTOMATED_MESSAGE = 'Automated Message:';

/**
 * Handy object to be passed to the ld-offer component.
 * Divides the updates per "stages".
 *
 * @param {Object} - The specific offer
 * @return {Object} The processed object to use
 */
export default function getOfferUpdatesData(offer) {
  if (isNil(offer) || isEmpty(offer.activities)) {
    return [];
  }

  const updates = getUpdates(offer.activities);
  const triggers = getTriggersIndexes(updates);

  return [
    { stage: OfferStateEnum.SETTLED,
      updates: getSettledUpdates(updates, triggers).reverse(),
      separator: null,
    },
    { stage: OfferStateEnum.CLOSED,
      updates: getClosedUpdates(updates, triggers).reverse(),
      separator: null,
    },
    { stage: OfferStateEnum.PENDING,
      updates: getPendingUpdates(updates, triggers).reverse(),
      separator: getSeparator(OfferStateEnum.PENDING, updates, triggers.pendingTrigger),
    },
    { stage: OfferStateEnum.APPLICATION,
      updates: getApplicationUpdates(updates, triggers).reverse(),
      separator: getSeparator(OfferStateEnum.APPLICATION, updates, triggers.applicationTrigger),
    },
    { stage: OfferStateEnum.CONTACT,
      updates: getContactUpdates(updates, triggers).reverse(),
      separator: getSeparator(OfferStateEnum.CONTACT, updates, triggers.contactTrigger),
    },
    { stage: OfferStateEnum.ACTIVE,
      updates: getAcceptedUpdates(updates, triggers),
      separator: null,
    },
  ];
}

/**
 * Gets the processed updates from the offer activities
 *
 * @param {Array} The activities
 * @returns {Object}
 */
function getUpdates(activities) {
  const updates = activities.map((update) => {
    const type = getUpdateType(update.activity_type);

    return {
      uuid: update.uuid,
      type,
      title: i18n.t(`dashboard.updates.title.${type}`),
      subtitle: getTimeDistance(update.activity_date),
      description: getUpdateDescription(type, update.description),
      footer: getShortDateString(update.activity_date),
      date: update.activity_date,
    };
  });

  return updates[0].type === UpdateTypeEnum.ACCEPTED
    ? updates
    : updates.reverse();
}

/**
 * Gets the correct description according to the update type
 *
 * @param {Object} type - an update type
 * @param {String} description
 * @returns {String}
 */
function getUpdateDescription(type, description) {
  if (type === UpdateTypeEnum.ACCEPTED) {
    return i18n.t('dashboard.updates.description.accept');
  }

  if (type === UpdateTypeEnum.CLOSED && description.includes(AUTOMATED_MESSAGE)) {
    return i18n.t('dashboard.updates.description.closed');
  }

  return description;
}

/**
 * Gets the separator data for a specific type
 *
 * @param {String} The type - any from the OfferStateEnum
 * @param {Array} The range of updates
 * @param {Number|null} The index of the trigger of a new range
 * @returns
 */
function getSeparator(type, updates, indexOfTrigger) {
  if (isNil(indexOfTrigger)) {
    return null;
  }

  return {
    title: i18n.t(`common.offer.state.uppercase.${type}`),
    subtitle: getDateString(updates[indexOfTrigger].date),
  };
}

/**
 * Gets the indexes of the stages' triggers
 * that will allows us to divide the updates into the "main stages"
 *
 * @param {Array} The updates, already processed.
 * @returns {Object}
 */
function getTriggersIndexes(updates) {
  const acceptedIndex = getIndex(updates, UpdateTypeEnum.ACCEPTED);
  const contactIndex = getIndex(updates, UpdateTypeEnum.CONTACT);
  const applicationIndex = getIndex(updates, UpdateTypeEnum.APPLICATION);
  const settledIndex = getIndex(updates, UpdateTypeEnum.SETTLED);
  const closedIndex = getIndex(updates, UpdateTypeEnum.CLOSED);

  // check if the contact was after other triggers
  const triggersAfterContact = [applicationIndex, settledIndex, closedIndex];
  const falseTrigger = triggersAfterContact.some((index) => !isNil(index) && index < contactIndex);

  return {
    contactTrigger: acceptedIndex,
    applicationTrigger: falseTrigger ? null : contactIndex,
    pendingTrigger: applicationIndex,
    settledTrigger: settledIndex,
    closedTrigger: closedIndex,
  };
}

/**
 * Gets the index of a given update type, from the collection of updates,
 * or false if it is not found.
 *
 * @param {Array} The updates, already processed.
 * @param {String} The type - any from the UpdateTypeEnum
 * @returns {Number|Null}
 */
function getIndex(updates, type) {
  const index = updates.findIndex((update) => update.type === type);

  return index === -1 ? null : index;
}

/**
 * Gets a range of updates given a min and max index, excluding the limits by default
 *
 * @param {Array}  updates
 * @param {Object} limits - { min, max}
 * @param {Object} options - {includeMin, includeMax}
 * @returns {Array}
 */
function getRange(updates, { min, max } = {}, { includeMin = false, includeMax = false } = {}) {
  if (isNil(min)) {
    return [];
  }

  const minLimit = includeMin ? min : min + 1;
  const maxLimit = includeMax && max ? max + 1 : max;

  return updates.slice(minLimit, maxLimit);
}

/**
 * Gets the accepted range of updates
 */
function getAcceptedUpdates(updates, { contactTrigger }) {
  return getRange(updates, { min: contactTrigger, max: 1 }, { includeMin: true });
}

/**
 * Gets the contact range of updates
 */
function getContactUpdates(updates, { contactTrigger, applicationTrigger, pendingTrigger }) {
  const options = { includeMax: true };

  // Legacy: if we skiped application stage
  if (isNil(applicationTrigger) && !isNil(pendingTrigger)) {
    return getRange(updates, { min: contactTrigger, max: pendingTrigger }, options);
  }

  if (isNil(applicationTrigger)) {
    return getRange(updates, { min: contactTrigger }, options);
  }

  return getRange(updates, { min: contactTrigger, max: applicationTrigger }, options);
}

/**
 * Gets the application range of updates
 */
function getApplicationUpdates(updates, { applicationTrigger, pendingTrigger }) {
  const options = { includeMax: true };

  if (isNil(pendingTrigger)) {
    return getRange(updates, { min: applicationTrigger }, options);
  }

  return getRange(updates, { min: applicationTrigger, max: pendingTrigger }, options);
}

/**
 * Gets the pending range of updates
 */
function getPendingUpdates(updates, { pendingTrigger, settledTrigger, closedTrigger }) {
  if (settledTrigger) {
    return getRange(updates, { min: pendingTrigger, max: settledTrigger });
  }

  if (closedTrigger) {
    return getRange(updates, { min: pendingTrigger, max: closedTrigger });
  }

  return getRange(updates, { min: pendingTrigger });
}

/**
 * Gets the settled range of updates
 */
function getSettledUpdates(updates, { settledTrigger }) {
  return getRange(updates, { min: settledTrigger }, { includeMin: true, includeMax: true });
}

/**
 * Gets the closed range of updates
 */
function getClosedUpdates(updates, { closedTrigger }) {
  return getRange(updates, { min: closedTrigger }, { includeMin: true, includeMax: true });
}
